import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	Button,
	Group,
	Stack,
	type TabItem,
	Tabs,
	TextInput,
} from '@ekhodealer/ekho-common/rev';

import { ExportPopup } from '@common/components/ExportPopup/ExportPopup';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { paths } from '@config/paths';
import { DiscountTab } from '@config/paths/discountTabs';

import { useExportDiscounts } from '../api/useExportDiscounts';
import { useSearchDiscounts } from '../api/useSearchDiscounts';
import { discountDrawerModeAtom, discountFormDirtyAtom } from '../atoms/discountDetailsDrawerAtoms';
import { DISCOUNT_FORM_DEFAULTS, discountFormAtom } from '../atoms/discountFormAtoms';
import {
	currentPageAtom,
	discountsParamsAtom,
	discountTabFilterAtom,
	searchValueAtom,
	selectedDiscountsAtom,
	updateSearchValueAtom,
} from '../atoms/discountsAtoms';
import { useDiscountNavigation } from '../hooks/useDiscountNavigation';

export function DiscountsToolbar() {
	const navigate = useNavigate();
	const routeParams = useParams<{ tabId: DiscountTab }>();
	const servicesProviderId = useCurrentServicesProviderId();

	// Filter atoms
	const searchValue = useAtomValue(searchValueAtom);
	const updateSearchValue = useSetAtom(updateSearchValueAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);

	// Export data
	const discountsParams = useAtomValue(discountsParamsAtom);
	const { data } = useSearchDiscounts(discountsParams);
	const selectedDiscounts = useAtomValue(selectedDiscountsAtom);
	const exportMutation = useExportDiscounts();

	// Create discount
	const { openCreateDiscount } = useDiscountNavigation();
	const setDrawerMode = useSetAtom(discountDrawerModeAtom);
	const setDiscountForm = useSetAtom(discountFormAtom);
	const setFormDirty = useSetAtom(discountFormDirtyAtom);

	// Get current active tab from URL params
	const activeTab = routeParams.tabId || DiscountTab.ALL;

	// Sync tab filter atom with URL params
	const setDiscountTabFilter = useSetAtom(discountTabFilterAtom);
	useEffect(() => {
		const tabFromUrl = routeParams.tabId || DiscountTab.ALL;
		setDiscountTabFilter(tabFromUrl);
	}, [routeParams.tabId, setDiscountTabFilter]);

	const handleTabSelect = (item: TabItem) => {
		if (!servicesProviderId) {
			return;
		}
		setCurrentPage(1);
		navigate(paths.discountsTab.getHref(servicesProviderId, item.id as DiscountTab));
	};

	const handleSearchChange = (value: string) => {
		updateSearchValue(value);
	};

	const handleCreateDiscount = () => {
		setDrawerMode('create');
		setDiscountForm(DISCOUNT_FORM_DEFAULTS);
		setFormDirty(false);
		openCreateDiscount();
	};

	const tabItems: TabItem[] = [
		{
			id: DiscountTab.ALL,
			content: 'All',
		},
		{
			id: DiscountTab.ACTIVE,
			content: 'Active',
		},
		{
			id: DiscountTab.SCHEDULED,
			content: 'Scheduled',
		},
		{
			id: DiscountTab.EXPIRED,
			content: 'Expired',
		},
	];

	return (
		<Stack itemsSpacing='16'>
			<Tabs items={tabItems} selectedKey={activeTab} onSelect={handleTabSelect} />
			<Group itemsAlignY='center' itemsAlignX='space-between' noWrap>
				<Group.Item>
					<TextInput
						placeholder='Search'
						type='search'
						size='small'
						value={searchValue}
						onChange={handleSearchChange}
					/>
				</Group.Item>
				<Group.Item>
					<Group itemsSpacing='8' noWrap>
						<ExportPopup
							resourceName='discounts'
							currentPageItems={data?.data ?? []}
							totalCount={data?.count ?? 0}
							selectedItems={selectedDiscounts}
							getItemId={(item) => item.id}
							onExport={(ids) =>
								exportMutation.mutateAsync({ discountIds: ids })
							}
							isPending={exportMutation.isPending}>
							<Button variant='secondary' size='medium'>
								Export
							</Button>
						</ExportPopup>
						<Button variant='filled' size='medium' onPress={handleCreateDiscount}>
							Create discount
						</Button>
					</Group>
				</Group.Item>
			</Group>
		</Stack>
	);
}

